import { BulkVisitsActionBar } from '@/components/bulkQueue/BulkVisitsActionBar';
import { bulkQueueRowToTarget } from '@/components/bulkQueue/bulkVisitTargets';
import { ActionsRenderCell } from '@/components/bulkQueue/cells/ActionsRender';
import { FulfillmentWindowRenderCell } from '@/components/bulkQueue/cells/FulfillmentWindowRender';
import { OrderNotesRenderCell, OrderRenderCell } from '@/components/bulkQueue/cells/OrderRender';
import { PatientRenderCell } from '@/components/bulkQueue/cells/PatientRender';
import { PriorityRenderCell } from '@/components/bulkQueue/cells/PriorityRender';
import { ClinicianRenderCell } from '@/components/bulkQueue/cells/clinician/ClinicianRender';
import { DateRenderCell } from '@/components/bulkQueue/cells/date/DateRender';
import {
    DateSelectionCell,
    DateSelectionHeader,
} from '@/components/bulkQueue/cells/date/DateSelection';
import { ServicesRenderCell } from '@/components/bulkQueue/cells/services/ServicesRender';
import { getSetCellProps } from '@/components/bulkQueue/tables/components';
import { VisitTableRow } from '@/components/bulkQueue/tables/queries';
import { BulkTentativeDateAssignmentTableQuery } from '@/components/bulkQueue/tables/tentativeDate/BulkTentativeDateAssignmentTable.gql.types';
import {
    BulkTentativeDateContextProvider,
    useBulkTentativeDateContext,
} from '@/components/bulkQueue/tables/tentativeDate/BulkTentativeDateContext';
import { useConnectedBulkTable } from '@/components/bulkQueue/tables/useConnectedBulkTable';
import { Pagination } from '@/components/bulkQueue/tables/useUnifiedPagination';
import { BulkTableRow } from '@/components/bulkQueue/types';
import { useBulkVisitActions } from '@/components/bulkQueue/useBulkVisitActions';
import { rowToLink } from '@/components/bulkQueue/utils';
import { MapFriendlyVisit } from '@/components/mapping/VisitsMap/VisitsMap.types';
import { useSetMapFriendlyVisits } from '@/components/mapping/VisitsMap/useSetMapFriendlyVisits';
import { AxleTable, RawColumn, ServerSideSort } from '@/components/tables/AxleTable';
import { EntrySource } from '@/components/visits/dialogs/ReassignVisits/v3/tracking/entrySource';
import { visitOrVisitRequestTableRowFragmentToMapFriendlyVisits } from '@/components/visits/utils/visitOrVisitRequestTableRowFragmentToMapFriendlyVisits';
import { PAGE_BODY_SKELETON_HEIGHT, SimpleSkeleton } from '@/fetching/suspense';
import { useHasVisitCancelPermission } from '@/hooks/permissions/visit';
import { useWideVisitTableTreatment } from '@/hooks/useWideVisitTableTreatment';
import { FeatureFlag } from '@/types/featureFlag';
import { useFeatureFlagEnabled } from '@/utils/useFeatureFlagEnabled';
import { gql } from '@apollo/client';
import { Typography } from '@mui/material';
import { FC, useCallback, useMemo } from 'react';

export const BULK_TENTATIVE_DATE_ASSIGNMENT_TABLE_QUERY = gql`
    query BulkTentativeDateAssignmentTable(
        $visitFilters: VisitFilter
        $visitPagination: PaginationInput
        $visitOrdering: VisitOrdering
    ) {
        visits(filters: $visitFilters, pagination: $visitPagination, ordering: $visitOrdering) {
            totalCount
            items {
                ...VisitTableRow
            }
        }
    }
    ${VisitTableRow}
`;

type BulkTentativeDateAssignmentTableProps = {
    rows: BulkTableRow[];
    pagination: Pagination;
    title?: string;
    serverSideSort: ServerSideSort;
    showSelection: boolean;
    isRowSelectable: (row: BulkTableRow) => boolean;
    selectedRowValues: string[];
    setSelectedRowValues: (values: string[]) => void;
};

const rowToSelectionId = (row: BulkTableRow) => row.id;

const getColumns = (
    hasEditableItems: boolean,
    rows: BulkTableRow[],
    hasOrders: boolean,
    pinActions: boolean,
): RawColumn<BulkTableRow>[] => [
    {
        field: 'sortPriority',
        label: 'Priority',
        rowToComponent: (row) => <PriorityRenderCell entity={row} />,
        options: { sort: false },
    },
    {
        field: 'patient.fullName',
        label: 'Patient',
        rowToComponent: (row) => <PatientRenderCell entity={row} />,
    },
    {
        field: 'sortServicesName',
        label: 'Services',
        rowToComponent: (row) => <ServicesRenderCell entity={row} />,
        options: { sort: false },
    },
    {
        field: 'cliniciansSortKey',
        label: 'Clinician',
        rowToComponent: (row) => <ClinicianRenderCell entity={row} />,
        options: { sort: false },
    },
    {
        field: 'sortDate',
        label: 'Date',
        rowToComponent: (row) =>
            row.__typename === 'Visit' && !row.permissions.reschedule ? (
                <DateRenderCell entity={row} />
            ) : (
                <DateSelectionCell entity={row} />
            ),
        options: {
            setCellProps: (_value: unknown, rowIndex: number) => {
                const row = rows[rowIndex];
                const isEditable = row.__typename !== 'Visit' || row.permissions.reschedule;
                return getSetCellProps(hasEditableItems && isEditable ? true : null);
            },
            customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
                <DateSelectionHeader
                    key={columnMeta.name + '-header'}
                    columnMeta={columnMeta}
                    handleToggleColumn={handleToggleColumn}
                    sortOrder={sortOrder}
                    hasEditableItems={hasEditableItems}
                />
            ),
        },
    },
    {
        field: 'fulfillmentWindow.startDate',
        label: 'Fulfillment Window',
        rowToComponent: (row) => <FulfillmentWindowRenderCell entity={row} />,
    },
    ...(hasOrders
        ? [
              {
                  field: 'orderSortKey',
                  label: 'Order',
                  rowToComponent: (row: BulkTableRow) => <OrderRenderCell entity={row} />,
                  options: { sort: false },
              },
              {
                  label: 'Order Notes',
                  rowToComponent: (row: BulkTableRow) => <OrderNotesRenderCell entity={row} />,
              },
          ]
        : []),
    {
        field: '',
        label: 'Actions',
        rowToComponent: (row) => <ActionsRenderCell entity={row} />,
        pinRight: pinActions,
    },
];

export const BulkTentativeDateAssignmentTable: FC<BulkTentativeDateAssignmentTableProps> = (
    props,
) => {
    const {
        rows,
        pagination,
        title,
        serverSideSort,
        showSelection,
        isRowSelectable,
        selectedRowValues,
        setSelectedRowValues,
    } = props;
    const hasEditableItems = rows.some((r) => r.__typename !== 'Visit' || r.permissions.reschedule);
    const hasOrders = useFeatureFlagEnabled(FeatureFlag.ORDERS);
    const { horizontalScrollButtons, pinActions } = useWideVisitTableTreatment();
    const columns = useMemo(
        () => getColumns(hasEditableItems, rows, hasOrders, pinActions),
        [hasEditableItems, rows, hasOrders, pinActions],
    );
    const { selectedCount } = useBulkTentativeDateContext();
    // Hide row selection (and its select-all header checkbox) when there are no
    // rows to select. AxleTable requires all selection props to agree.
    const showBulkSelect = showSelection && rows.length > 0;
    return (
        <AxleTable
            stickyHeader
            horizontalScrollButtons={horizontalScrollButtons}
            title={
                typeof title === 'string' ? (
                    <Typography variant='h2'>{title}</Typography>
                ) : undefined
            }
            dynamicPaging={pagination}
            data={rows}
            toolbar={false}
            rowToLink={rowToLink}
            columns={columns}
            serverSideSort={serverSideSort}
            selectableRows={showBulkSelect ? 'multiple' : 'none'}
            disableSelection={selectedCount > 0}
            rowToSelectionIdentifier={showBulkSelect ? rowToSelectionId : undefined}
            isRowSelectable={showBulkSelect ? isRowSelectable : undefined}
            selectedRowValues={showBulkSelect ? selectedRowValues : undefined}
            setSelectedRowValues={showBulkSelect ? setSelectedRowValues : undefined}
        />
    );
};

type ConnectedBulkTentativeDateAssignmentTableProps = {
    title?: string;
};

export const ConnectedBulkTentativeDateAssignmentTable: FC<
    ConnectedBulkTentativeDateAssignmentTableProps
> = (props) => {
    const { title } = props;
    const { rows, pagination, serverSideSort, showSkeleton } =
        useConnectedBulkTable<BulkTentativeDateAssignmentTableQuery>({
            query: BULK_TENTATIVE_DATE_ASSIGNMENT_TABLE_QUERY,
        });
    const bulkCancelEnabled = useFeatureFlagEnabled(
        FeatureFlag.DASHBOARD_BULK_CANCELLATION_ALL_VISITS,
    );
    const hasBulkCancel = useHasVisitCancelPermission() && bulkCancelEnabled;

    // Reassign isn't offered from this queue, so there's no entry source to tag.
    const bulk = useBulkVisitActions({
        rows,
        toTarget: bulkQueueRowToTarget,
        capabilities: { reassign: false, cancel: hasBulkCancel },
        entrySource: EntrySource.Untagged,
    });

    const setMapFriendlyVisits: () => MapFriendlyVisit[] = useCallback(() => {
        return visitOrVisitRequestTableRowFragmentToMapFriendlyVisits(rows);
    }, [rows]);

    useSetMapFriendlyVisits(setMapFriendlyVisits);

    if (showSkeleton) {
        return <SimpleSkeleton height={PAGE_BODY_SKELETON_HEIGHT} />;
    }

    return (
        <BulkTentativeDateContextProvider entities={rows} disabled={bulk.selectedIds.length > 0}>
            {bulk.showSelection && <BulkVisitsActionBar {...bulk.actionBarProps} />}
            <BulkTentativeDateAssignmentTable
                rows={rows}
                title={title}
                pagination={pagination}
                serverSideSort={serverSideSort}
                showSelection={bulk.showSelection}
                isRowSelectable={(row) => bulk.isSelectableId(row.id)}
                selectedRowValues={bulk.selectedIds}
                setSelectedRowValues={bulk.setSelectedIds}
            />
        </BulkTentativeDateContextProvider>
    );
};
